import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  forwardRef,
  OnDestroy,
  QueryList,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RadioComponent } from '../radio/radio.component';

@Component({
  selector: 'lib-radio-group',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioGroupComponent),
      multi: true,
    },
  ],
})
export class RadioGroupComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  @ContentChildren(RadioComponent, { descendants: true }) radios: QueryList<RadioComponent>;

  private _value: any;
  private _destroyed$ = new Subject<void>();
  private onChange?: (value: any) => void;
  private onTouched?: () => void;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.updateRadios();

    this.radios.changes.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.subscribeToRadios();
      this.updateRadios();
    });

    this.subscribeToRadios();
  }

  private subscribeToRadios(): void {
    this.radios.forEach((radio) => {
      radio.change.pipe(takeUntil(this._destroyed$)).subscribe((value) => {
        this._value = value;
        this.updateRadios();
        this.onChange?.(value);
        this.onTouched?.();
      });
    });
  }

  writeValue(value: any): void {
    this._value = value;
    this.updateRadios();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.radios?.forEach((r) => r.setDisabled(isDisabled));
    this.cdr.markForCheck();
  }

  private updateRadios(): void {
    this.radios?.forEach((radio) => {
      radio.setSelected(radio.value === this._value);
    });
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
